import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Plus, X, Trash2, Radio, Save, Megaphone, Target as TargetIco, DollarSign } from "lucide-react";
import { sr } from "../../lib/sr";

const field = { width: "100%", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 9px", fontSize: 13, background: "#fff" };
const CAT_ORDER = ["purchase_intent", "brand_interest", "contextual", "competitor_location", "first_party", "funasia"];

function Section({ title, icon, right, children }) {
  return (
    <div className="sr-card" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {icon}<div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{title}</div>{right}
      </div>
      {children}
    </div>
  );
}

function TargetingSection({ pid, channels, ai }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const [cat, setCat] = useState(null);

  const load = async () => {
    const r = await sr.proposalTargetingRecommend(pid);
    setList(r.targeting || []);
  };
  useEffect(() => { sr.getProposal(pid).then((r) => setList((r.proposal.draft || {}).targeting || [])); }, [pid]);

  const recommend = async () => {
    setBusy("rec");
    try { const r = await sr.proposalTargetingRecommend(pid, true); setList(r.targeting); toast.success("Targeting recommended from the live catalog"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't recommend targeting"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await sr.proposalTargetingSave(pid, list); setList(r.targeting); toast.success("Targeting saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const patch = (idx, k, v) => setList((xs) => xs.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));
  const remove = (idx) => setList((xs) => xs.filter((_, i) => i !== idx));
  const addFromCatalog = async (q) => {
    setBusy("cat");
    try {
      const r = await sr.proposalTargetingCatalog(pid, q ? { q } : {});
      setCat(r.groups || []);
    } catch (e) { toast.error("Catalog unavailable"); }
    finally { setBusy(""); }
  };
  const addItem = (it, category) => {
    const ch = Object.keys(it.cpm || {}).length ? "display" : "display";
    setList((xs) => [...(xs || []), {
      id: Math.random().toString(36).slice(2, 12), catalog_id: it.id, name: it.name, category,
      category_label: ({ purchase_intent: "Purchase-intent audiences", brand_interest: "Brand-interest audiences", contextual: "Contextual targeting", competitor_location: "Competitor-location audiences", first_party: "Approved first-party & retargeting", funasia: "FunAsia radio, social & community" })[category] || category,
      why: "", geography: "", method: it.type_title || "Audience segment", channel: ch, message: "", cta: "Learn more",
      outcome: "", size_label: it.audience_size_label, availability: "available", availability_note: "", editable: true,
    }]);
    toast.success("Added — edit the copy, then Save");
  };

  if (list === null) return <Section title="Targeting recommendations" icon={<TargetIco size={18} />}><div className="sr-muted" style={{ fontSize: 13 }}>Loading…</div></Section>;

  const grouped = {};
  list.forEach((c, i) => { (grouped[c.category] = grouped[c.category] || []).push({ ...c, _i: i }); });

  return (
    <Section title="Targeting recommendations" icon={<TargetIco size={18} />}
      right={<div style={{ display: "flex", gap: 8 }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => addFromCatalog()} disabled={!!busy} data-testid="pp-add-catalog"><Plus size={13} /> Add from catalog</button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={recommend} disabled={!!busy} data-testid="pp-recommend-targeting">{busy === "rec" ? <Loader2 size={13} className="sr-spin" /> : <Sparkles size={13} />} {list.length ? "Re-recommend" : "Recommend targeting"}</button>
        {list.length > 0 && <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={save} disabled={!!busy} data-testid="pp-save-targeting">{busy === "save" ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />} Save</button>}
      </div>}>
      {list.length === 0 && <div className="sr-muted" style={{ fontSize: 13 }}>No audiences yet. Recommend from the live catalog or add your own — every segment is a real catalogue entry; supplier names &amp; costs are never shown.</div>}
      {ai?.audience_strategy && (
        <div data-testid="pp-ai-audience-strategy" style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
          <Sparkles size={13} style={{ color: "#7C3AED", marginTop: 2, flexShrink: 0 }} />
          <span style={{ fontSize: 11.5, color: "#5B21B6" }}><strong>From the AI Growth Plan:</strong> {ai.audience_strategy}</span>
        </div>
      )}
      <div data-testid="pp-targeting">
        {CAT_ORDER.filter((c) => grouped[c]).map((c) => (
          <div key={c} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>{grouped[c][0].category_label}</div>
            {grouped[c].map((card) => (
              <div key={card.id} data-testid={`tgt-card-${card.id}`} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <input style={{ ...field, fontWeight: 600 }} value={card.name || ""} onChange={(e) => patch(card._i, "name", e.target.value)} />
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(card._i)} data-testid={`tgt-remove-${card.id}`}><Trash2 size={13} /></button>
                </div>
                <textarea style={{ ...field, marginTop: 6 }} rows={2} placeholder="Why it fits this brand" value={card.why || ""} onChange={(e) => patch(card._i, "why", e.target.value)} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 6 }}>
                  <select style={field} value={card.channel || "display"} onChange={(e) => patch(card._i, "channel", e.target.value)}>
                    {channels.map((ch) => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
                  </select>
                  <input style={field} placeholder="Geography" value={card.geography || ""} onChange={(e) => patch(card._i, "geography", e.target.value)} />
                  <input style={field} placeholder="Call to action" value={card.cta || ""} onChange={(e) => patch(card._i, "cta", e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
                  <input style={field} placeholder="Creative message" value={card.message || ""} onChange={(e) => patch(card._i, "message", e.target.value)} />
                  <input style={field} placeholder="Intended outcome" value={card.outcome || ""} onChange={(e) => patch(card._i, "outcome", e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="sr-badge sr-badge-green" style={{ fontSize: 10 }}>{card.availability === "available" ? "Available" : "Subject to availability"}</span>
                  {card.size_label && <span className="sr-muted" style={{ fontSize: 10.5 }}>Est. scale: {card.size_label}</span>}
                  <select style={{ ...field, width: "auto", padding: "3px 6px", fontSize: 11 }} value={card.availability} onChange={(e) => patch(card._i, "availability", e.target.value)}>
                    <option value="available">Available</option>
                    <option value="subject_to_availability">Subject to availability</option>
                  </select>
                </div>
                {card.availability_note && <div className="sr-muted" style={{ fontSize: 10.5, marginTop: 4 }}>{card.availability_note}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>
      {cat && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setCat(null)}>
          <div className="sr-card" style={{ width: 640, maxHeight: "80vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="catalog-modal">
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}><div style={{ fontWeight: 700, flex: 1 }}>Add from the live audience catalogue</div><button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setCat(null)}><X size={14} /></button></div>
            {cat.length === 0 && <div className="sr-muted" style={{ fontSize: 13 }}>No matches — try the Recommend button.</div>}
            {cat.map((g) => (
              <div key={g.category} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>{g.category_label}</div>
                {g.items.map((it) => (
                  <div key={it.id} style={{ display: "flex", gap: 8, alignItems: "center", borderBottom: "1px solid var(--border)", padding: "6px 0" }}>
                    <div style={{ flex: 1, fontSize: 12.5 }}>{it.name}<span className="sr-muted" style={{ fontSize: 10.5 }}> · {it.type_title}{it.audience_size_label ? ` · ${it.audience_size_label}` : ""}</span></div>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => addItem(it, g.category)}><Plus size={12} /> Add</button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function CompetitorSection({ pid, draft }) {
  const [c, setC] = useState(draft.competitor || { brands: [], locations: [], signals: { purchase_intent: true, brand_interest: true, visitation: false }, lookback_days: 30 });
  const [busy, setBusy] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const save = async () => {
    setBusy("save");
    try { const r = await sr.proposalCompetitorSave(pid, c); setC(r.competitor); toast.success("Competitor plan saved"); }
    catch (e) { toast.error("Couldn't save"); } finally { setBusy(""); }
  };
  const suggest = async () => {
    if (!newBrand.trim()) { toast.error("Enter a competitor brand first"); return; }
    setBusy("sug");
    try {
      const r = await sr.proposalCompetitorLocations(pid, newBrand.trim());
      if (!r.suggestions?.length) toast.info("No live location suggestions — add locations manually.");
      setC((x) => ({ ...x, locations: [...(x.locations || []), ...(r.suggestions || [])] }));
    } catch (e) { toast.error("Suggestions unavailable"); } finally { setBusy(""); }
  };
  const addBrand = () => { if (newBrand.trim()) { setC((x) => ({ ...x, brands: [...(x.brands || []), { name: newBrand.trim() }] })); setNewBrand(""); } };
  const addLoc = () => setC((x) => ({ ...x, locations: [...(x.locations || []), { name: "", type: "competitor" }] }));

  return (
    <Section title="Competitor targeting" icon={<Megaphone size={18} />}
      right={<button className="sr-btn sr-btn-gold sr-btn-sm" onClick={save} disabled={!!busy} data-testid="pp-save-competitor">{busy === "save" ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />} Save</button>}>
      <div data-testid="pp-competitor">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input style={field} placeholder="Add competitor brand (e.g. Omega)" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} data-testid="comp-brand-input" />
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={addBrand} data-testid="comp-add-brand"><Plus size={13} /> Brand</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={suggest} disabled={!!busy} data-testid="comp-suggest-loc">{busy === "sug" ? <Loader2 size={12} className="sr-spin" /> : null} Suggest locations</button>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {(c.brands || []).map((b, i) => <span key={i} className="sr-badge" style={{ fontSize: 11 }}>{b.name} <X size={10} style={{ cursor: "pointer" }} onClick={() => setC((x) => ({ ...x, brands: x.brands.filter((_, j) => j !== i) }))} /></span>)}
        </div>
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, flex: 1 }}>Locations</div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={addLoc} data-testid="comp-add-location"><Plus size={12} /> Location</button>
        </div>
        {(c.locations || []).map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginTop: 6 }} data-testid={`comp-loc-${i}`}>
            <input style={field} placeholder="Location name / address" value={l.name || ""} onChange={(e) => setC((x) => ({ ...x, locations: x.locations.map((y, j) => j === i ? { ...y, name: e.target.value } : y) }))} />
            <select style={{ ...field, width: 170 }} value={l.type || "competitor"} onChange={(e) => setC((x) => ({ ...x, locations: x.locations.map((y, j) => j === i ? { ...y, type: e.target.value } : y) }))}>
              <option value="competitor">Competitor</option>
              <option value="partner_retailer">Partner retailer</option>
            </select>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setC((x) => ({ ...x, locations: x.locations.filter((_, j) => j !== i) }))}><X size={12} /></button>
          </div>
        ))}
        <div style={{ marginTop: 10, fontWeight: 600, fontSize: 12.5 }}>Signals</div>
        <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
          {[["purchase_intent", "Purchase intent"], ["brand_interest", "Brand interest"], ["visitation", "Physical visitation"]].map(([k, lbl]) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
              <input type="checkbox" checked={!!(c.signals || {})[k]} onChange={(e) => setC((x) => ({ ...x, signals: { ...(x.signals || {}), [k]: e.target.checked } }))} data-testid={`comp-sig-${k}`} /> {lbl}
            </label>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>Lookback
            <select style={{ ...field, width: 90 }} value={c.lookback_days || 30} onChange={(e) => setC((x) => ({ ...x, lookback_days: parseInt(e.target.value) }))} data-testid="comp-lookback">
              {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </label>
        </div>
        <div className="sr-muted" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
          A multi-brand retailer that also carries this brand is a <b>distribution partner</b>, not a competitor — set its type accordingly. We reach eligible audiences near these places within the lookback window; we never identify individual visitors or imply access to every competitor customer.
        </div>
      </div>
    </Section>
  );
}

function MediaPlanSection({ pid, draft, ai }) {
  const [plan, setPlan] = useState(draft.media_plan || null);
  const [busy, setBusy] = useState("");
  const build = async () => {
    setBusy("build");
    try { const r = await sr.proposalMediaPlanRecommend(pid, !!plan); setPlan(r.media_plan); toast.success(r.from_growth_plan ? "Media plan built from the AI Growth Plan channel mix" : "Media plan built — FunAsia Radio included"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build plan"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { const r = await sr.proposalMediaPlanSave(pid, plan); setPlan(r.media_plan); toast.success("Plan saved & repriced"); }
    catch (e) { toast.error("Couldn't save"); } finally { setBusy(""); }
  };
  const patchCh = (key, k, v) => setPlan((p) => ({ ...p, channels: p.channels.map((c) => c.key === key ? { ...c, [k]: v } : c) }));
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <Section title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>Media plan &amp; pricing{plan?.from_growth_plan && <span className="sr-badge" data-testid="mp-from-gp" style={{ fontSize: 10, background: "#EDE9FE", color: "#6D28D9" }}>From AI Growth Plan</span>}</span>} icon={<DollarSign size={18} />}
      right={<div style={{ display: "flex", gap: 8 }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={build} disabled={!!busy} data-testid="pp-build-mediaplan">{busy === "build" ? <Loader2 size={13} className="sr-spin" /> : <Sparkles size={13} />} {plan ? "Rebuild" : "Build media plan"}</button>
        {plan && <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={save} disabled={!!busy} data-testid="pp-save-mediaplan">{busy === "save" ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />} Save &amp; reprice</button>}
      </div>}>
      {!plan && <div className="sr-muted" style={{ fontSize: 13 }} data-testid="pp-mediaplan-empty">No plan yet. Build a recommended channel mix — FunAsia Radio is included for in-community reach; Google &amp; Meta appear as optional add-ons that require account setup.</div>}
      {plan && (
        <div data-testid="pp-mediaplan">
          {plan.channels.map((c) => (
            <div key={c.key} data-testid={`mp-row-${c.key}`} style={{ display: "grid", gridTemplateColumns: "20px 1.4fr 90px 100px 100px", gap: 8, alignItems: "center", padding: "7px 0", borderBottom: "1px solid var(--border)", opacity: c.included ? 1 : 0.55 }}>
              <input type="checkbox" checked={!!c.included} onChange={(e) => patchCh(c.key, "included", e.target.checked)} data-testid={`mp-include-${c.key}`} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {c.key === "funasia_radio" && <Radio size={13} style={{ color: "var(--gold)" }} />}{c.label}
                  {c.requires_setup && <span className="sr-badge" style={{ fontSize: 9.5, background: "#FEF3C7", color: "#92400E" }}>requires setup</span>}
                </div>
                <div className="sr-muted" style={{ fontSize: 10.5 }}>{c.role}{c.est_impressions ? ` · ~${c.est_impressions.toLocaleString()} impressions (${c.est_method})` : ""}</div>
              </div>
              <div className="sr-muted" style={{ fontSize: 11 }}>{c.unit === "cpm" ? "CPM" : c.unit === "spots" ? "Radio spots" : c.unit}</div>
              <input style={{ ...field, textAlign: "right" }} type="number" value={c.media_spend} onChange={(e) => patchCh(c.key, "media_spend", parseFloat(e.target.value) || 0)} data-testid={`mp-spend-${c.key}`} />
              <input style={{ ...field, textAlign: "right" }} type="number" placeholder="creative" value={c.content_charge} onChange={(e) => patchCh(c.key, "content_charge", parseFloat(e.target.value) || 0)} data-testid={`mp-content-${c.key}`} />
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12.5, display: "flex", alignItems: "center", gap: 6 }}>Management fee %
              <input style={{ ...field, width: 80 }} type="number" value={plan.fees?.management_fee_pct ?? 15} onChange={(e) => setPlan((p) => ({ ...p, fees: { ...(p.fees || {}), management_fee_pct: parseFloat(e.target.value) || 0 } }))} data-testid="mp-mgmt-pct" />
            </label>
            {plan.pricing?.needs_approval && <span className="sr-badge" style={{ fontSize: 10.5, background: "#FEE2E2", color: "#991B1B" }} data-testid="mp-needs-approval">Discount below floor — needs approval</span>}
          </div>
          <div style={{ marginTop: 12, background: "#F8FAFC", borderRadius: 10, padding: 12 }} data-testid="mp-total">
            <Row l="Media (all-in)" v={money(plan.pricing?.media_only)} />
            <Row l="Creative / content" v={money(plan.pricing?.content_total)} />
            <Row l={`Management fee (${plan.pricing?.management_fee_pct}%)`} v={money(plan.pricing?.management_fee)} />
            {plan.pricing?.platform_fee ? <Row l="Platform fee" v={money(plan.pricing?.platform_fee)} /> : null}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}><Row l={<b>Total proposed investment</b>} v={<b>{money(plan.pricing?.total_customer_cost)}</b>} /></div>
            <div className="sr-muted" style={{ fontSize: 10.5, marginTop: 6 }}>Media-only vs. all-in are shown separately. Internal supplier costs and margins are never included in client exports.</div>
          </div>
        </div>
      )}
    </Section>
  );
}

function Row({ l, v }) {
  return <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "2px 0" }}><span>{l}</span><span>{v}</span></div>;
}

export default function ProposalPlan({ pid, draft, ai }) {
  const [channels, setChannels] = useState([]);
  useEffect(() => { sr.proposalChannels().then((r) => setChannels(r.channels || [])).catch(() => {}); }, []);
  return (
    <div data-testid="proposal-plan">
      <TargetingSection pid={pid} channels={channels} ai={ai} />
      <CompetitorSection pid={pid} draft={draft} />
      <MediaPlanSection pid={pid} draft={draft} ai={ai} />
    </div>
  );
}
